<template>
  <div
    id="saleForceForm"
    class="mb-8 grid gap-4 mobile:grid-cols-1"
    :class="{ 'desktop:grid-flow-col': form1 }"
  >
    <EditableArea
      v-if="form1"
      class="desktop:col-span-1"
      :content="form1"
    />
    <div class="rounded border border-neutral-100 p-6 mobile:px-4 desktop:col-span-2">
      <EditableArea
        v-if="form2"
        :content="form2"
      />
      <form
        action="https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8&orgId=00DWz000005tvij"
        method="POST"
      >
        <KikDynamicForm
          ref="dynamicForm"
          :values="form"
          :fields="fields"
          @on-form-update="onFormUpdate"
        >
          <input
            type="hidden"
            name="orgid"
            value="00DWz000005tvij"
          >
          <input
            type="hidden"
            name="retURL"
            :value="currentPage"
          >
          <div>
            <EditableArea
              v-if="form3"
              class="mb-4 font-light"
              :content="form3"
            />
            <KikButton
              class="min-w-40 mobile:min-w-full"
              :disabled="errors || loading"
              v-bind="{ type: 'submit' }"
            >
              <SfLoaderCircular v-if="loading" />
              <span v-else>{{ switchable?.btnLabel }}</span>
            </KikButton>
          </div>
        </KikDynamicForm>
      </form>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { EditableArea } from '@magnolia/vue-editor';
import { SfLoaderCircular } from '@storefront-ui/vue';
import { Props } from './Form.model';
import useUiNotification from '~/composables/useUiNotification';

const {
  form3,
  form2,
  form1,
  switchable,
} = defineProps(Props);

const { $domain, $i18n } = useNuxtApp();

const route = useRoute();
const { send } = useUiNotification();

const dynamicForm = ref();

const form: any = ref({});

const onFormUpdate = (value: unknown) => {
  form.value = value;
};

const currentPage = computed(() => {
  const url = new URL(`https://${$domain.host}${route.fullPath}`);
  url.searchParams.append('success', 'true');

  const query = Array.from(url.searchParams.entries())
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
});

onBeforeMount(() => {
  if (route.query?.success) {
    send({ message: $i18n.t('Your message was submitted with success'), icon: 'check2', type: 'success' });
  }
});

const fields = computed(() => ({
  '00Ndr000000RXxZ': {
    type: 'option',
    value: switchable?.salutationOptions?.map(key => ({ key, label: key })) || [],
    rules: 'required',
    label: 'Salutation',
  },
  '00Ndr000000RZuX': {
    type: 'text',
    label: 'Name',
    rules: 'required|minmax:2,80',
  },
  'email': {
    type: 'text',
    label: 'E-mail address',
    rules: 'required|minmax:2,80|email',
    onChange: 'trim',
  },
  '00Ndr000000RbgD': {
    type: 'text',
    label: 'Phone number',
    rules: 'minmax:2,40',
    onChange: 'trim',
  },
  // is needed as name and id for salesforceteam
  '00Ndr0000008Wab': {
    label: 'Subject',
    type: 'option',
    value: switchable?.subjectOptions?.map((option) => {
      return {
        ...option,
        key: option.key,
      };
    }),
  },
  '00Ndr000000AUxh': {
    type: 'text',
    label: 'Order number',
    rules: 'minmax:2,255',
    onChange: 'number',
  },
  'subject': {
    type: 'text',
    label: 'Other topics',
    rules: 'minmax:2,80',
  },
  'description': {
    type: 'textArea',
    rules: 'required',
    label: 'Message',
  },
}));
</script>
